import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";
import type { NieuwsArtikel } from "./nieuws-shared";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const leesNieuws = createServerFn({ method: "GET" }).inputValidator(z.object({ slug: z.string().regex(/^[a-z0-9-]+$/).nullable() })).handler(async ({ data }) => {
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"];
  const url = process.env["SUPABASE_URL"];
  if (!key || !url) throw new Error("Nieuws kan momenteel niet worden geladen.");
  const client = createClient<Database>(url, key, { auth: { persistSession: false }, global: { fetch: (input, init) => {
    const headers = new Headers(init?.headers);
    if (key.startsWith("sb_") && headers.get("Authorization") === `Bearer ${key}`) headers.delete("Authorization");
    headers.set("apikey", key);
    return fetch(input, { ...init, headers });
  } } });
  let query = client.from("nieuws_artikelen").select("id,slug,titel,beschrijving,zoekwoord,dienst_slug,inhoud,bronnen,gepubliceerd_op,gewijzigd_op").lte("gepubliceerd_op", new Date().toISOString()).order("gepubliceerd_op", { ascending: false });
  if (data.slug) query = query.eq("slug", data.slug);
  const result = await query.limit(data.slug ? 1 : 500);
  if (result.error) throw new Error("Nieuws kan momenteel niet worden geladen.");
  return (result.data ?? []) as unknown as NieuwsArtikel[];
});
export const nieuwsQuery = () => queryOptions({ queryKey: ["nieuws"], queryFn: () => leesNieuws({ data: { slug: null } }), staleTime: 60_000 });

export const nieuwsBeheer = createServerFn({ method: "GET" }).middleware([requireSupabaseAuth]).handler(async ({ context }) => {
  const role = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
  if (role.error || !role.data) throw new Error("Alleen beheerders hebben toegang.");
  const result = await context.supabase.from("nieuws_automatisering").select("status,reden,hervatten,laatste_poging,laatste_publicatie").eq("id", "dagelijks").maybeSingle();
  if (result.error) throw new Error(result.error.message);
  return result.data;
});
export const hervatNieuws = createServerFn({ method: "POST" }).middleware([requireSupabaseAuth]).handler(async ({ context }) => {
  const role = await context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" });
  if (role.error || !role.data) throw new Error("Alleen beheerders hebben toegang.");
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const result = await supabaseAdmin.from("nieuws_automatisering").upsert({ id: "dagelijks", status: "actief", reden: null, hervatten: "handmatig" });
  if (result.error) throw new Error(result.error.message);
  return { ok: true };
});